import { z } from 'zod'
import type { FeatureRequirements, Features, RequiredFeatures } from './features.ts'
import { FeaturesSchema, featureKeys } from './features.ts'

type AnonymousActor = {
  kind: 'anonymous'
  isAdmin: false
  isMntn: false
}

type UserActor<TFeatures extends object = Features> = {
  kind: 'user'
  userId: number
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber?: string
  orgSlug?: string
  isAdmin: boolean
  isMntn: boolean
  features: TFeatures
}

type ServiceActor = {
  kind: 'service'
  serviceId: string
  userId: number
  isAdmin: true
  isMntn: true
}

type AgentActor = {
  kind: 'agent'
  agentId: string
  isAdmin: boolean
  isMntn: true
}

export type Actor<TFeatures extends object = Features> =
  | AnonymousActor
  | UserActor<TFeatures>
  | ServiceActor
  | AgentActor
type ActorKind = Actor['kind']
export type ActorFromKind<K extends ActorKind> = Extract<Actor, { kind: K }>
export type Kind<K extends ActorKind = ActorKind> = { actor: ActorFromKind<K> }

export const UserSchema = z.custom<Kind<'user'>>(
  value => z.object({ actor: z.object({ kind: z.literal('user') }) }).safeParse(value).success,
)

export const ServiceSchema = z.custom<Kind<'service'>>(
  value => z.object({ actor: z.object({ kind: z.literal('service') }) }).safeParse(value).success,
)

export const AgentSchema = z.custom<Kind<'agent'>>(
  value => z.object({ actor: z.object({ kind: z.literal('agent') }) }).safeParse(value).success,
)

export function AgentIdSchema<const AgentId extends string>(agentId: AgentId) {
  const schema = z.object({
    actor: z.object({ agentId: z.literal(agentId) }),
  })

  return z.custom<{ actor: { agentId: AgentId } }>(value => schema.safeParse(value).success)
}

export type IsMntn = { actor: Actor & { isMntn: true } }
export const IsMntnSchema = z.custom<IsMntn>(
  value => z.object({ actor: z.object({ isMntn: z.literal(true) }) }).safeParse(value).success,
)

export type IsAdmin = { actor: Actor & { isAdmin: true } }
export const IsAdminSchema = z.custom<IsAdmin>(
  value => z.object({ actor: z.object({ isAdmin: z.literal(true) }) }).safeParse(value).success,
)

export function HasFeaturesSchema<const Requirements extends FeatureRequirements>(requirements: Requirements) {
  const schema = z.object({ actor: z.object({ features: FeaturesSchema }) })

  return z.custom<HasFeatures<Requirements>>(value => {
    const result = schema.safeParse(value)
    if (!result.success) return false

    return featureKeys.every(feature => {
      const required = requirements[feature]
      return required === undefined || result.data.actor.features[feature].enabled === required
    })
  })
}

export type HasFeatures<Requirements extends FeatureRequirements> = {
  actor: { features: RequiredFeatures<Requirements> }
}
